import { Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { type AutomationRuleRow } from './automationRuleTableModel';
import { useAorText } from 'utils/aorTerminology';

const SEARCH_LABEL = 'Search automation rules…';

/**
 * Presentational: takes rows that are already mapped, sorted and filtered so it
 * stays free of the react-query and axios chain behind the rule hooks.
 */
export function AutomationRuleTable({
  rows,
  search,
  onSearchChange,
  onSelect,
}: {
  /** Already filtered by the caller. */
  rows: AutomationRuleRow[];
  search: string;
  onSearchChange: (next: string) => void;
  onSelect: (id: string) => void;
}) {
  const aorText = useAorText();
  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <Input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={SEARCH_LABEL}
        aria-label={SEARCH_LABEL}
        className="tw:h-8"
      />
      <div className="tw:overflow-x-auto tw:rounded-md tw:border tw:border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rule</TableHead>
              <TableHead className="tw:w-[170px]">Last Updated</TableHead>
              <TableHead className="tw:min-w-[220px]">Trigger</TableHead>
              <TableHead className="tw:w-[130px]">Event Type</TableHead>
              <TableHead className="tw:w-[150px]">Check Frequency</TableHead>
              <TableHead className="tw:w-[120px]">Has Notification</TableHead>
              <TableHead className="tw:w-[160px]">
                Has Response Checklist
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="tw:text-xs tw:text-muted-foreground"
                >
                  No rules match “{search}”.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Edit ${row.name}`}
                  className="tw:cursor-pointer"
                  onClick={() => onSelect(row.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(row.id);
                    }
                  }}
                >
                  <TableCell className="tw:align-top">
                    <div className="tw:flex tw:flex-col tw:gap-1">
                      <span className="tw:text-sm tw:leading-snug">
                        {row.name}
                      </span>
                      <Badge
                        variant={row.enabled ? 'default' : 'outline'}
                        className="tw:w-fit tw:text-[10px]"
                      >
                        {row.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="tw:align-top tw:text-xs tw:text-muted-foreground">
                    {formatDateTime(row.updatedAt)}
                  </TableCell>
                  <TableCell
                    className="tw:align-top tw:text-xs tw:whitespace-normal"
                    title={aorText(row.trigger)}
                  >
                    <span className="tw:line-clamp-2">
                      {aorText(row.trigger)}
                    </span>
                  </TableCell>
                  <TableCell className="tw:align-top tw:text-xs">
                    {row.eventType}
                  </TableCell>
                  <TableCell className="tw:align-top tw:text-xs tw:text-muted-foreground">
                    {row.checkFrequency}
                  </TableCell>
                  <TableCell className="tw:align-top">
                    <BooleanCell value={row.hasNotification} />
                  </TableCell>
                  <TableCell className="tw:align-top">
                    <BooleanCell value={row.hasChecklist} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/** Icon-only would read as blank to a screen reader, hence the sr-only word. */
function BooleanCell({ value }: { value: boolean }) {
  return value ? (
    <>
      <Check className="tw:h-4 tw:w-4 tw:text-foreground" aria-hidden />
      <span className="tw:sr-only">Yes</span>
    </>
  ) : (
    <>
      <span aria-hidden className="tw:text-muted-foreground">
        —
      </span>
      <span className="tw:sr-only">No</span>
    </>
  );
}
